import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  RefreshCw,
  UserCheck,
  UserX
} from 'lucide-react';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employee_id: '',
    name: '',
    employee_type: 'Operator'
  });

  useEffect(() => {
    fetchEmployees();
    fetchEmployeeTypes();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await API.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeTypes = async () => {
    try {
      const response = await API.get('/employee-types');
      // Extract just the names for the dropdown
      setEmployeeTypes(response.data.map(t => t.name));
    } catch (error) {
      setEmployeeTypes(['Operator', 'General Staff', 'Office', 'Casual', 'Supervisor', 'Apprentice']);
    }
  };

  const handleSave = async () => {
    if (!form.employee_id || !form.name) {
      toast.error('Employee ID and name are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingEmployee) {
        await API.put(`/employees/${editingEmployee.id}`, {
          name: form.name,
          employee_type: form.employee_type
        });
        toast.success('Employee updated');
      } else {
        await API.post('/employees', form);
        toast.success('Employee created');
      }
      setDialogOpen(false);
      setEditingEmployee(null);
      setForm({ employee_id: '', name: '', employee_type: 'Operator' });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (emp) => {
    setEditingEmployee(emp);
    setForm({
      employee_id: emp.employee_id,
      name: emp.name,
      employee_type: emp.employee_type
    });
    setDialogOpen(true);
  };

  const handleDelete = async (empId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      await API.delete(`/employees/${empId}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleToggleActive = async (emp) => {
    try {
      await API.put(`/employees/${emp.id}`, {
        is_active: !emp.is_active
      });
      toast.success(emp.is_active ? 'Employee deactivated' : 'Employee activated');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update employee status');
    }
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = 
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || emp.employee_type === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'Operator': return 'bg-blue-100 text-blue-700';
      case 'General Staff': return 'bg-green-100 text-green-700';
      case 'Office': return 'bg-purple-100 text-purple-700';
      case 'Casual': return 'bg-amber-100 text-amber-700';
      case 'Supervisor': return 'bg-indigo-100 text-indigo-700';
      case 'Apprentice': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter(e => e.is_active).length,
    operators: employees.filter(e => e.employee_type === 'Operator').length,
    general: employees.filter(e => e.employee_type === 'General Staff').length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Employees</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Manage employee classifications for payroll processing</p>
        </div>
        <Button onClick={() => {
          setEditingEmployee(null);
          setForm({ employee_id: '', name: '', employee_type: 'Operator' });
          setDialogOpen(true);
        }} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Employee
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Employees</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-semibold text-green-600">{stats.active}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Operators</p>
                <p className="text-2xl font-semibold text-blue-600">{stats.operators}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-700">Full Allowances</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">General Staff</p>
                <p className="text-2xl font-semibold text-green-600">{stats.general}</p>
              </div>
              <Badge className="bg-green-100 text-green-700">Base Only</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>How Employee Types Work:</strong> During payroll processing, rules with specific Employee Types only apply to matching employees. 
          Employees not in this list default to &quot;Operator&quot; type and receive all applicable allowances.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Employee List</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchEmployees}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {employeeTypes.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{searchTerm || filterType !== 'all' ? 'No employees match your filters' : 'No employees configured'}</p>
              <p className="text-sm">Add employees to control which allowances apply during payroll</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((emp) => (
                  <TableRow key={emp.id} className={!emp.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-mono text-sm">{emp.employee_id}</TableCell>
                    <TableCell className="font-medium">{emp.name}</TableCell>
                    <TableCell>
                      <Badge className={getTypeColor(emp.employee_type)}>
                        {emp.employee_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {emp.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(emp)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(emp)}
                          title={emp.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {emp.is_active ? (
                            <UserX className="w-4 h-4 text-amber-500" />
                          ) : (
                            <UserCheck className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(emp.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>
              Configure employee classification to control which rules and allowances apply during payroll.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee ID *</Label>
              <Input
                placeholder="e.g., EMP001 or payroll ID"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                disabled={!!editingEmployee}
              />
              <p className="text-xs text-slate-500">Must match the ID from imported timesheets/dockets</p>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="e.g., Kevin Russell"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Employee Type</Label>
              <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {employeeTypes.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                This determines which rules apply to this employee during payroll processing.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {editingEmployee ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
